/**
 * Pick a day (no time). Themed popover calendar, Monday first. The clear
 * button sits beside the trigger rather than inside it, so the markup stays
 * valid (the shared DateTimePicker nests a button in a button).
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { addDays, addMonths, fromYmd, todayYmd, weekStart } from "./lib";

export function DayPicker({
  value, onChange, placeholder = "Pick a day", className, clearable = true,
}: { value: string; onChange: (day: string) => void; placeholder?: string; className?: string; clearable?: boolean }) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => (value || todayYmd()).slice(0, 7));
  const [up, setUp] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const today = todayYmd();

  useEffect(() => { if (open) setMonth((value || todayYmd()).slice(0, 7)); }, [open, value]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);
  useLayoutEffect(() => {
    if (!open || !trigger.current) return;
    const r = trigger.current.getBoundingClientRect();
    setUp(window.innerHeight - r.bottom < 330 && r.top > window.innerHeight - r.bottom);
  }, [open]);

  const first = `${month}-01`;
  const start = weekStart(first);
  const days = Array.from({ length: 42 }, (_, i) => addDays(start, i));
  const label = value ? fromYmd(value).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: value.slice(0, 4) === today.slice(0, 4) ? undefined : "numeric" }) : "";

  return (
    <div ref={ref} className={cn("relative flex items-center", className)}>
      <button
        ref={trigger}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex h-9 w-full items-center gap-2 rounded-lg border bg-zinc-900/50 px-3 text-left text-sm transition-colors hover:bg-zinc-900",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/30",
          open ? "border-indigo-500/50" : "border-zinc-800",
          clearable && value && "pr-8",
        )}
      >
        <CalendarIcon className="h-3.5 w-3.5 shrink-0 text-zinc-500" />
        <span className={cn("flex-1 truncate", value ? "text-zinc-100" : "text-zinc-500")}>{label || placeholder}</span>
      </button>
      {clearable && value && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label="Clear the day"
          className="absolute right-1.5 inline-flex h-6 w-6 items-center justify-center rounded text-zinc-500 hover:bg-zinc-800 hover:text-rose-300"
        >
          <X className="h-3 w-3" />
        </button>
      )}
      {open && (
        <div className={cn(
          "absolute left-0 z-40 w-72 rounded-xl border border-zinc-800 bg-zinc-950 p-3 shadow-xl shadow-shade/40 animate-fade-in",
          up ? "bottom-full mb-1" : "top-full mt-1",
        )}>
          <div className="mb-2 flex items-center justify-between">
            <button type="button" onClick={() => setMonth(addMonths(first, -1).slice(0, 7))} className="rounded-md p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100" aria-label="Previous month">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-semibold text-zinc-100">{fromYmd(first).toLocaleDateString(undefined, { month: "long", year: "numeric" })}</span>
            <button type="button" onClick={() => setMonth(addMonths(first, 1).slice(0, 7))} className="rounded-md p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100" aria-label="Next month">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] font-semibold uppercase text-zinc-500">
            {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => <div key={i} className="py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {days.map((d) => {
              const inMonth = d.startsWith(month);
              const sel = d === value;
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => { onChange(d); setOpen(false); }}
                  className={cn(
                    "h-8 rounded-lg text-xs tabular-nums transition-colors",
                    sel ? "cc-brand-bg font-semibold text-white" : d === today ? "font-semibold text-indigo-300 ring-1 ring-inset ring-indigo-400/40 hover:bg-zinc-800" : inMonth ? "text-zinc-200 hover:bg-zinc-800" : "text-zinc-600 hover:bg-zinc-800/60",
                  )}
                >
                  {fromYmd(d).getDate()}
                </button>
              );
            })}
          </div>
          <div className="mt-2 flex items-center justify-between border-t border-zinc-800 pt-2">
            <button type="button" onClick={() => { onChange(today); setOpen(false); }} className="text-xs font-medium text-indigo-300 hover:underline">Today</button>
            {clearable && value && <button type="button" onClick={() => { onChange(""); setOpen(false); }} className="text-xs text-zinc-500 hover:text-zinc-200">No date</button>}
          </div>
        </div>
      )}
    </div>
  );
}
